import { Col, Row } from "react-bootstrap";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";
import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import RenderToast from "@/components/RenderToast";
import { reviewsApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";
import AsyncDropdown from "@/components/AsyncDropdown/AsyncDropdown";
import useModalGetOptions from "@/CustomHooks/Modal/useModalGetOptions";
import { LIST_MODE, LIST_PER_PAGE } from "@/constant/Lists/listMode";
import DropDown from "@/components/DropDown/DropDown";
import { webUsersApi } from "@/apis/apis";
import { ratingOptions } from "@/constant/Reviews/reviews-entity";
import ReviewSchema from "@/schema/ReviewManagement/ReviewSchema";
import { ReviewInitialValues } from "@/data/ReviewManagement/initial-values";

const ReviewModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id ? `${reviewsApi}/${modalState.id}` : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Sport ${action} successfully.`,
    });
  };

  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${reviewsApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });

  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: reviewsApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });

  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: ReviewInitialValues(modalState.id ? showData : null),
    validationSchema: ReviewSchema(!!modalState.id),
    onSubmit: (values) => handleSubmit(values),
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    formData.append("from_user_id", values.from_user_id);
    formData.append("to_user_id", values.to_user_id);
    formData.append("rating", values.rating);
    formData.append("comment", values.comment);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  const baseQueryParams = `?mode=${LIST_MODE.SELECT}&perPage=${LIST_PER_PAGE}&filter[keyword]=`;
  const userListApi = `${webUsersApi}${baseQueryParams}`;
  const defaultAdminLabelFormatter = useCallback((item) => item.username, []);

  const {
    loading: fromUserLoading,
    defaultOptions: fromUserDefaultOptions,
    debouncedFetchOptions: fromUserFetchOptions,
    getSelectedOption: fromUserGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: userListApi,
    formikValue: formik.values.from_user_id,
    formikInitialValue: formik.initialValues.from_user_id,
    showData: !!modalState?.id && showData?.data?.from_user_id,
    labelFormatter: defaultAdminLabelFormatter,
  });

  const {
    loading: toUserLoading,
    defaultOptions: toUserDefaultOptions,
    debouncedFetchOptions: toUserFetchOptions,
    getSelectedOption: toUserGetSelectedOption,
  } = useModalGetOptions({
    modalState,
    listApi: userListApi,
    formikValue: formik.values.to_user_id,
    formikInitialValue: formik.initialValues.to_user_id,
    showData: !!modalState?.id && showData?.data?.to_user_id,
    labelFormatter: defaultAdminLabelFormatter,
  });

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <BasicModal
      header={modalState.id ? "Edit Review" : "Add Review"}
      onHide={closeModal}
      show={modalState.isOpen}
      saveLoading={saveIsPending}
      formik={formik}
    >
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="From User"
            placeholder="No User selected. Type to search..."
            defaultOptions={fromUserDefaultOptions}
            loadOptions={fromUserFetchOptions}
            isLoading={fromUserLoading}
            value={fromUserGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "from_user_id",
                selected?.value || formik.values.from_user_id || null
              );
            }}
            errorText={
              (formik.touched.from_user_id && formik.errors.from_user_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.from_user_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AsyncDropdown
            label="To User"
            placeholder="No User selected. Type to search..."
            defaultOptions={toUserDefaultOptions}
            loadOptions={toUserFetchOptions}
            isLoading={toUserLoading}
            value={toUserGetSelectedOption()}
            onChange={(selected) => {
              formik.setFieldValue(
                "to_user_id",
                selected?.value || formik.values.to_user_id || null
              );
            }}
            errorText={
              (formik.touched.to_user_id && formik.errors.to_user_id) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.to_user_id?.join(" "))
            }
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <DropDown
            mainClass="flex flex-row"
            dropDownContainer="w-[450px] z-[10000]"
            labelClassName="flex-grow"
            label="Rating"
            value={ratingOptions.find(
              (item) => item.value === formik.values.rating
            )}
            setValue={(selected) =>
              formik.setFieldValue("rating", selected.value)
            }
            options={ratingOptions}
            errorText={
              (formik.touched.rating && formik.errors.rating) ||
              (saveError?.status === 422 &&
                saveError?.response?.data?.errors?.rating?.join(" "))
            }
          />
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <BasicInput
            formik={formik}
            fieldName={"comment"}
            saveError={saveError}
            label="Comment"
            type="textarea"
          />
        </Col>
      </Row>
    </BasicModal>
  );
});

export default ReviewModal;
